import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Box,
  Switch,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormikContext } from "formik";
import FormikFroalaTextEditor from "../../../FroalaEditor/children/FormikFroalaTextEditor";
import { handleAiAssistantIcon } from "../../../FroalaEditor/children/FroalaHelpers";
import ReactDOM from "react-dom";
import NewAIPrompt from "../../../NewAiPrompt";
import AddIcon from "@mui/icons-material/Add";
import { colors } from "../../../../utils";
import { transformedText } from "../../../../utils/helpers";

const AddExternalLinks = ({
  form,
  push,
  remove,
  name,
  addMore = false,
  showError = false,
}) => {
  const { setFieldValue, errors, touched, handleBlur, setFieldTouched } =
    useFormikContext();

  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [checkedState, setCheckedState] = useState({});

  const editorRef = useRef(null);

  useEffect(() => {
    if (Object.keys(checkedState).length === 0) {
      const initialCheckedState = {};
      form.values[name].forEach((item, index) => {
        initialCheckedState[index] = item.is_cta_enabled || false;
      });
      setCheckedState(initialCheckedState);
    }
  }, []);

  const handleToggleAccordion = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleSwitchChange = (index, event) => {
    const isChecked = event.target.checked;

    if (!isChecked) {
      setExpandedIndex(null);
    }

    setCheckedState({
      ...checkedState,
      [index]: isChecked,
    });

    isChecked && setExpandedIndex(index);

    setFieldValue(`${name}.${index}.is_cta_enabled`, isChecked);
    setFieldTouched(`${name}.${index}.is_cta_enabled`, true, false);
  };

  const handleFroalaCustomization = (FroalaEditor) => {
    handleAiAssistantIcon(FroalaEditor, setShowAiPrompt);
  };

  const handleLinkChange = (index, field, value) => {
    setFieldValue(`${name}.${index}.${field}`, value);
  };

  const handleLinkBlur = (index) => (e) => {
    // First call the original handleBlur
    handleBlur(e);

    // Then trim spaces from the link value
    const currentValue = form.values[name][index].link;
    const trimmedValue = currentValue.trim();

    // Only update if trimming actually changed something
    if (trimmedValue !== currentValue) {
      setFieldValue(`${name}.${index}.link`, trimmedValue);
    }
  };

  const handleAddExternal = () => {
    push({ title: "", link: "", icon: "", is_cta_enabled: false });
  };

  return (
    <Box sx={{ pb: "30px" }}>
      {form.values[name].map((link, index) => (
        <Accordion
          expanded={expandedIndex === index}
          onChange={() => handleToggleAccordion(index)}
          key={index}
          sx={{
            mt: 2,
            boxShadow: "none", // Disable the box-shadow (border effect)
            "&:before": {
              display: "none", // Remove the default divider line
            },
            "&.Mui-expanded": {
              margin: 0, // Ensure no margin when expanded
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            sx={{
              margin: 0,

              "& .MuiAccordionSummary-content": {
                margin: 0,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch
                  name={`${name}.${index}.is_cta_enabled`}
                  checked={checkedState[index] || false}
                  onChange={(e) => handleSwitchChange(index, e)}
                  onClick={(e) => e.stopPropagation()}
                  inputProps={{ "aria-label": "controlled" }}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#8F38EF",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#8F38EF",
                    },
                  }}
                />

                <Typography
                  sx={{
                    color: "#0F0F0F",
                    fontSize: "20px",
                    fontWeight: "700",
                    lineHeight: "20px",
                    fontFamily: "DM Sans",
                  }}
                >
                  {transformedText(link.title) || `Button ${index + 1}`}
                </Typography>
              </Box>
              {/***
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  remove(index);
                }}
                style={{
                  border: "none",
                  backgroundColor: colors.white,
                  color: colors.error,
                }}
              >
                <DeleteIcon />
              </button>
               ***/}
            </Box>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              padding: "0px 16px",
            }}
          >
            <Typography
              sx={{
                color: "#123051",
                fontSize: "16px",
                fontWeight: "500",
                lineHeight: "20px",
                fontFamily: "DM Sans",
              }}
            >
              Text
            </Typography>

            <FormikFroalaTextEditor
              editorHeight={"65px"}
              name={`${name}.${index}.title`}
              id={1}
              handleEditorCustomization={handleFroalaCustomization}
              editorRef={editorRef}
              toolbarButtons={[
                "fontFamily",
                "fontSize",
                "textColorCustom",
                "bold",
                "italic",
                "underline",
                "aiAssistant",
              ]}
              characters="40"
            />

            {showError && (
              <Typography color={colors.error}>
                {errors.business_box_cta
                  ? errors.business_box_cta[index]?.title
                  : "" || errors.external_links
                  ? errors.external_links[index]?.title
                  : ""}
              </Typography>
            )}
            <Typography
              sx={{
                color: "#123051",
                fontSize: "16px",
                fontWeight: "500",
                lineHeight: "20px",
                fontFamily: "DM Sans",
              }}
            >
              Link
            </Typography>

            <TextField
              variant="outlined"
              className="noBorder"
              value={link.link}
              name={`${name}.${index}.link`}
              onBlur={handleLinkBlur(index)}
              onChange={(e) => handleLinkChange(index, "link", e.target.value)}
              placeholder="Paste link here"
              fullWidth
              InputProps={{
                sx: {
                  height: "52px",
                  border: "1px solid #123051",
                  fontFamily: "DM Sans, sans-serif", // Set font-family her
                  color: "#565556",
                  borderRadius: "8px",
                },
                inputProps: {
                  style: {
                    fontSize: "14px",
                    padding: "16px",
                  },
                },
              }}
            />
            {showError && (
              <Typography color={colors.error}>
                {errors.business_box_cta
                  ? errors.business_box_cta[index]?.link
                  : "" || errors.external_links
                  ? errors.external_links[index]?.link
                  : ""}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {addMore && (
        <Box mt={2} sx={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            onClick={handleAddExternal}
            sx={{
              border: "1px solid #E1E1E1", // Add a border with your preferred color
              color: "#1976d2", // Change the color of the icon
              borderRadius: "4px", // Add rounded corners
              backgroundColor: "#F6F7F9",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.08)", // Optional: Add a hover effect
              },
            }}
          >
            <AddIcon sx={{ color: "#8F38EF" }} />
          </IconButton>
        </Box>
      )}

      {showAiPrompt &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              zIndex: 6522299,
              right: "35vw",
              top: "25vh",
              overflow: "auto",
            }}
          >
            <NewAIPrompt
              modalWidth="350px"
              onCloseIconPress={() => setShowAiPrompt(false)}
              completionType={"title"}
            />
          </div>,
          document.body // You can also use a different element if needed
        )}
    </Box>
  );
};

export default AddExternalLinks;
